import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";
import CustomError from "@components/Error/CustomError";
import { MetaDescriptorProps } from "@features/seo/utils/types";
import { Route } from "@route-types/Podcasts/PodcastsKeywordPage/+types/PodcastsKeywordPage";
import PodcastsKeywordContent from "@routes/Podcasts/PodcastsKeywordPage/PodcastsKeywordContent";
import { fetchPodcastKeywordData } from "@routes/Podcasts/PodcastsKeywordPage/PodcastsKeywordPage.server";
import { PodcastKeywordContext } from "@routes/Podcasts/PodcastsKeywordPage/utils/types";
import { fetchContext, ResponseType } from "@sphtech/web2-core/ssr";
import { createMetaTags } from "@sphtech/web2-seo-schema";
import { getEnvironment } from "@util/constant";
import { getEnvironmentDomain } from "@util/helpers";
import { HttpStatusCode } from "axios";

/** This is the main entry point component for /keywords/:keyword route. */
const PodcastsKeywordPage = (): React.ReactElement => {
  const context = useLoaderData<PodcastKeywordContext>();

  // StatusCode is not 200
  if (context && context.statusCode !== HttpStatusCode.Ok) {
    return <CustomError statusCode={context.statusCode} />;
  }

  // Check if payload is missing
  if (typeof context?.payload === "undefined") {
    return <CustomError statusCode={HttpStatusCode.NotFound} />;
  }

  return (
    <PodcastsKeywordContent
      {...context.payload.data}
      gaProps={context.payload.gaProps}
    />
  );
};

export default PodcastsKeywordPage;

export const loader = async ({ request }: Route.LoaderArgs) => {
  const url = new URL(request.url, `http://localhost`);
  const path = url.pathname;
  const envDomain = getEnvironmentDomain(getEnvironment());
  const absolutePath = `${envDomain}${path}`;

  return await fetchPodcastKeywordData(absolutePath);
};

export const meta = ({ loaderData }: Route.MetaArgs): MetaDescriptorProps[] => {
  const context = loaderData as PodcastKeywordContext;

  if (context.type !== ResponseType.SUCCESS) return [];

  const data = context.payload;

  if (!data) return [];
  if (!data.metaTags) return [];

  return createMetaTags(data.metaTags);
};

export const clientLoader = async (args: ClientLoaderFunctionArgs) => {
  const result = (await fetchContext(args)) as {
    context?: PodcastKeywordContext;
  };

  if (!result.context) return await args.serverLoader();

  return result.context;
};
